import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../../utils/axios';
import { ChevronDown, Building2, Search, X } from 'lucide-react';

/**
 * CompanySelector Component with Lazy Loading
 * 
 * Allows field agents to quickly switch between assigned companies
 * Features:
 * - Lazy loading with infinite scroll
 * - Backend search (debounced)
 * - Dismissible dropdown (allows browsing catalog without selection)
 * 
 * Props:
 * - selectedCompanyId: Current selected company ID
 * - onCompanyChange: Callback when company is changed
 * - draftCounts: Object mapping company_id -> item count in draft
 */
const CompanySelector = ({ selectedCompanyId, onCompanyChange, draftCounts = {} }) => {
  // Dropdown state (closed by default to allow browsing catalog)
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Lazy loading state
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  const observer = useRef();
  const lastCompanyElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  // Auto-open dropdown when no company is selected
  useEffect(() => {
    if (!selectedCompanyId && isOpen) {
      // Load companies when dropdown opens
      if (companies.length === 0) {
        fetchCompanies(1, '');
      }
    }
  }, [selectedCompanyId, isOpen]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch companies when search changes
  useEffect(() => {
    setCompanies([]);
    setPage(1);
    setHasMore(true);
    fetchCompanies(1, debouncedSearch);
  }, [debouncedSearch]);

  // Fetch more companies when page changes
  useEffect(() => {
    if (page > 1) {
      fetchCompanies(page, debouncedSearch);
    }
  }, [page]);

  const fetchCompanies = async (pageNum, search) => {
    if (loading) return;
    
    try {
      setLoading(true);
      const response = await axios.get('/api/field-agent/companies', {
        params: {
          page: pageNum,
          limit: 50,
          search: search || undefined
        }
      });
      
      const newCompanies = (response.data.companies || []).map(item => ({
        id: item.company?.id || item.id,
        name: item.company?.name || item.name,
        business_number: item.company?.business_number || item.business_number,
        ...(item.company || item)
      }));
      
      setCompanies(prev => pageNum === 1 ? newCompanies : [...prev, ...newCompanies]);
      setTotalCount(response.data.total || 0);
      setHasMore(newCompanies.length === 50); // If we got full page, there might be more
    } catch (error) {
      console.error('Error fetching companies:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // Get selected company (might need to fetch it separately if not in list)
  const [selectedCompany, setSelectedCompany] = useState(null);
  
  useEffect(() => {
    if (selectedCompanyId) {
      const found = companies.find(c => c.id === selectedCompanyId);
      if (found) {
        setSelectedCompany(found);
      } else {
        // Fetch selected company details
        axios.get(`/api/field-agent/companies/${selectedCompanyId}`)
          .then(res => {
            const company = res.data.company || res.data;
            setSelectedCompany(company);
          })
          .catch(err => console.error('Error fetching selected company:', err));
      }
    } else {
      setSelectedCompany(null);
    }
  }, [selectedCompanyId, companies]);

  const handleSelect = (companyId) => {
    setSearchTerm('');
    if (companyId !== selectedCompanyId) {
      onCompanyChange(companyId);
      // Close dropdown after selection
      setIsOpen(false);
  }
  };

  return (
    <div className="relative w-full max-w-2xl" dir="rtl">
      {/* Selected Company Display - Compact */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full bg-white border rounded-lg p-2 flex items-center justify-between hover:border-primary-600 transition-all shadow-sm hover:shadow-md ${
          selectedCompany ? 'border-primary-500' : 'border-yellow-400 bg-yellow-50'
        }`}
      >
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-md ${selectedCompany ? 'bg-primary-100' : 'bg-yellow-100'}`}>
            <Building2 className={`h-4 w-4 ${selectedCompany ? 'text-primary-600' : 'text-yellow-600'}`} />
          </div>
          <div className="text-right">
            {selectedCompany ? (
              <>
                <div className="text-sm font-semibold text-gray-900">
                  {selectedCompany.name}
                </div>
                {draftCounts[selectedCompanyId] > 0 && (
                  <div className="text-xs text-primary-600 font-medium">
                    {draftCounts[selectedCompanyId]} בטיוטה
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-yellow-700 font-semibold">
                בחר חברה כדי להתחיל • {companies.length} חברות זמינות
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!selectedCompany && companies.length > 0 && (
            <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full font-bold animate-pulse">
              חובה לבחור
            </span>
          )}
        <ChevronDown 
          className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop - Always closeable to allow catalog browsing */}
          <div 
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown Menu */}
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-20 max-h-96 overflow-hidden">
            {/* Header */}
            <div className="p-3 border-b border-gray-200 sticky top-0 bg-gradient-to-b from-primary-50 to-white z-10">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary-600" />
                  <span className="text-sm font-bold text-gray-900">
                    בחר חברה ({companies.length}{totalCount > companies.length ? `/${totalCount}` : ''})
                  </span>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  aria-label="סגור"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
              
            {/* Search */}
              <div className="relative">
                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="חיפוש לפי שם או ח.פ..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pr-9 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  autoFocus
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                    aria-label="נקה חיפוש"
                  >
                    <X className="h-3 w-3 text-gray-400" />
                  </button>
                )}
              </div>
            </div>

            {/* Companies List */}
            <div className="overflow-y-auto max-h-80">
              {loading && companies.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="animate-spin inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full mb-3"></div>
                  <p className="text-sm text-gray-600">טוען חברות...</p>
                </div>
              ) : companies.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-3">
                    <Building2 className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-900 mb-1">לא נמצאו חברות</p>
                  <p className="text-xs text-gray-500">
                    {searchTerm ? 'נסה חיפוש אחר' : 'אין חברות מוקצות'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {companies.map((company, index) => {
                    // Attach observer to last element for infinite scroll
                    const isLast = index === companies.length - 1;
                    return (
                  <button
                    key={company.id}
                        ref={isLast ? lastCompanyElementRef : null}
                    onClick={() => handleSelect(company.id)}
                        className={`w-full p-3 flex items-center justify-between hover:bg-primary-50 transition-all ${
                          company.id === selectedCompanyId ? 'bg-primary-100 border-r-4 border-primary-600' : ''
                    }`}
                  >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className={`p-2 rounded-lg flex-shrink-0 ${
                        company.id === selectedCompanyId 
                              ? 'bg-primary-200' 
                          : 'bg-gray-100'
                      }`}>
                            <Building2 className={`h-5 w-5 ${
                          company.id === selectedCompanyId 
                                ? 'text-primary-700' 
                            : 'text-gray-600'
                        }`} />
                      </div>
                          <div className="text-right flex-1 min-w-0">
                            <div className={`text-sm font-semibold truncate ${
                              company.id === selectedCompanyId ? 'text-primary-900' : 'text-gray-900'
                            }`}>
                          {company.name}
                        </div>
                        {company.business_number && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            ח.פ: {company.business_number}
                          </div>
                        )}
                      </div>
                    </div>
                    
                        <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Draft Cart Indicator */}
                    {draftCounts[company.id] > 0 && (
                            <div className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-bold shadow-sm">
                        {draftCounts[company.id]}
                      </div>
                    )}
                    
                    {/* Selected Indicator */}
                    {company.id === selectedCompanyId && (
                            <div className="w-2 h-2 bg-primary-600 rounded-full animate-pulse"></div>
                    )}
                        </div>
                  </button>
                    );
                  })}
                  
                  {/* Loading indicator for infinite scroll */}
                  {loading && companies.length > 0 && (
                    <div className="p-4 text-center">
                      <div className="animate-spin inline-block w-6 h-6 border-3 border-primary-600 border-t-transparent rounded-full"></div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            {companies.length > 0 && (
              <div className="p-3 bg-gradient-to-t from-gray-50 to-white border-t border-gray-200">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">
                    {searchTerm ? (
                      `נמצאו ${companies.length} תוצאות`
                    ) : totalCount > 0 ? (
                      `מציג ${companies.length} מתוך ${totalCount} חברות`
                    ) : (
                      `${companies.length} חברות`
                    )}
                    {hasMore && !loading && ' • גלול למטה לעוד'}
                  </span>
                  {!selectedCompanyId && (
                    <span className="text-yellow-700 font-semibold animate-pulse">
                      ⚠️ בחר חברה
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CompanySelector;

